/**
 * Standardized popup/modal color scheme and styles
 */
export const POPUP_COLORS = {
  background: '#B4D9D4',      // Main popup background
  fieldBackground: '#EBF5F5', // Input field background
  buttonBackground: '#EBF5F5', // Button background
  buttonText: '#3A7380',       // Button text color
  titleText: '#366A75',        // Title text color
  buttonHover: '#D0E7E5',      // Button hover color
  closeBtnHover: '#2D5A60',    // Close button hover color
};

export const POPUP_STYLES = {
  container: 'fixed inset-0 bg-black/50 flex items-center justify-center z-[1400] p-4',
  modal: 'bg-[#B4D9D4] rounded-3xl px-8 pb-8 w-full shadow-2xl relative max-w-md',
  headerContainer: 'relative pt-1 pb-5',
  headerInner: 'flex items-center justify-center',
  title: 'text-2xl font-bold text-[#366A75]',
  closeButton: 'absolute right-0 text-[#366A75] hover:text-[#2D5A60] transition-colors disabled:opacity-50',
  input: 'w-full px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent',
  button: 'px-8 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none',
};

// MUI sx-based style objects for ClinicDialog
export const CLINIC_DIALOG_SX = {
  paper: {
    borderRadius: '16px',
    backgroundColor: POPUP_COLORS.background,
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
  },
  title: {
    color: POPUP_COLORS.titleText,
    fontSize: '1.5rem',
    fontWeight: 'bold',
    textAlign: 'center',
    borderBottom: '1px solid rgba(54, 106, 117, 0.1)',
    paddingBottom: '16px',
  },
  content: { pt: 3 },
  actions: {
    borderTop: '1px solid rgba(54, 106, 117, 0.1)',
    padding: '16px',
    gap: '8px',
    justifyContent: 'center',
  },
  fieldLabel: { color: POPUP_COLORS.titleText, fontWeight: 500, whiteSpace: 'nowrap', minWidth: '110px' },
  fieldRow: { display: 'flex', alignItems: 'center', gap: 2 },
  select: {
    backgroundColor: POPUP_COLORS.fieldBackground,
    borderRadius: '8px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: POPUP_COLORS.background },
  },
  textField: {
    backgroundColor: POPUP_COLORS.fieldBackground,
    borderRadius: '8px',
    color: POPUP_COLORS.titleText,
  },
  inputLabel: { color: POPUP_COLORS.titleText },
  primaryButton: {
    backgroundColor: POPUP_COLORS.fieldBackground,
    color: POPUP_COLORS.buttonText,
    '&:hover': { backgroundColor: POPUP_COLORS.buttonHover },
    borderRadius: '8px',
    textTransform: 'none' as const,
    fontWeight: '500',
    px: 3,
  },
  cancelButton: {
    backgroundColor: POPUP_COLORS.fieldBackground,
    color: POPUP_COLORS.buttonText,
    '&:hover': { backgroundColor: POPUP_COLORS.buttonHover },
    borderRadius: '8px',
    textTransform: 'none' as const,
    fontWeight: '500',
  },
  spinner: { color: POPUP_COLORS.titleText },
  infoText: { color: POPUP_COLORS.titleText, fontWeight: 500 },
};

// Modal width constants (Tailwind max-width classes)
export const MODAL_WIDTHS = {
  default: 'max-w-md',        // 28rem (448px)
  wide: 'max-w-lg',            // 32rem (512px) - ~20% wider than default
  extra_wide: 'max-w-xl',      // 36rem (576px)
  xxl: 'max-w-2xl',            // 42rem (672px)
  xxxl: 'max-w-3xl',           // 48rem (768px)
  xxxxl: 'max-w-4xl',          // 56rem (896px)
  full: 'max-w-5xl',           // 64rem (1024px)
  pdfViewer: 'w-[80vw]',       // 80% of viewport width
};
